import React from 'react';
import { CheckCircle, Wrench, Clock, Warning, Key, ClipboardText, Flag } from '@phosphor-icons/react';
import { BRAND_LOGO } from './Badges';
import {
  STAGE_COLS, STAGE_LABELS, deliveryStatus, deliveryNote,
  LOCATION_LABELS, statusLabel,
} from '../helpers';

/**
 * One car on the Delivery Board. Status rail + badge, the stages that apply to
 * this car as a track, a plain-language note, and one primary action.
 */

const STATUS_LABEL = {
  READY: 'READY', BLOCKED: 'BLOCKED', IN_PROGRESS: 'IN PROGRESS', NOT_STARTED: 'NOT STARTED',
};
const STATUS_CLASS = {
  READY: 'ready', BLOCKED: 'blocked', IN_PROGRESS: 'prog', NOT_STARTED: 'idle',
};
// status_* value → the track's visual state
const STAGE_STATE = { done: 'done', prog: 'active', ns: 'pending' };
const NOTE_ICON = { ok: CheckCircle, work: Wrench, wait: Clock, block: Warning };
const NOTE_WEIGHT = { ok: 'fill', work: 'fill', wait: 'duotone', block: 'fill' };

export default function DeliveryCard({ vehicle, onOpen, onFlag, onHandOver }) {
  const v = vehicle;
  const status = deliveryStatus(v);
  const cls = STATUS_CLASS[status];
  const note = deliveryNote(v);
  const NoteIcon = NOTE_ICON[note.kind];
  const ready = status === 'READY';
  const logo = BRAND_LOGO[v.brand];

  // Only the stages this car actually needs — no row of dashes.
  const stages = STAGE_COLS
    .map((col, i) => ({ col, label: STAGE_LABELS[i], state: STAGE_STATE[v[col]] }))
    .filter((s) => s.state);

  return (
    <div className={`dcard ${cls}`}>
      <div className="dcard-head" onClick={() => onOpen(v.id)}>
        <div className="dcard-top">
          {logo
            ? <img className="dcard-logo" src={logo} alt={v.brand} />
            : <span className="dcard-brandtxt">{v.brand}</span>}
          <span className={`dcard-badge ${cls}`}>{STATUS_LABEL[status]}</span>
        </div>
        <div className="dcard-idline">
          <span className="dcard-model">{v.model}</span>
          <span className={`dcard-loc ${v.location}`}>{LOCATION_LABELS[v.location] || v.location}</span>
        </div>
        <div className="dcard-stock num">
          {v.stock_number}{v.ro_number ? ` · RO ${v.ro_number}` : ''}
        </div>
      </div>

      {stages.length > 0 && (
        <div className="dcard-track">
          <div className="dcard-bars">
            {stages.map((s) => (
              <span key={s.col} className={`dcard-bar ${s.state}`} title={`${s.label}: ${statusLabel(v[s.col])}`} />
            ))}
          </div>
          <div className="dcard-bars">
            {stages.map((s) => (
              <span key={s.col} className={`dcard-slbl ${s.state}`}>{s.label}</span>
            ))}
          </div>
        </div>
      )}

      <div className={`dcard-note ${note.kind}`}>
        <NoteIcon size={13} weight={NOTE_WEIGHT[note.kind]} />
        <span>{note.text}</span>
      </div>

      <div className="dcard-foot">
        {ready ? (
          <button className="dcard-cta go" onClick={() => onHandOver(v)}>
            <Key size={15} weight="fill" />Hand over
          </button>
        ) : (
          <button className="dcard-cta" onClick={() => onOpen(v.id)}>
            <ClipboardText size={15} weight="duotone" />Checklist
          </button>
        )}
        <button
          className={`dcard-flag ${v.blocker_note ? 'on' : ''}`}
          onClick={() => onFlag(v)}
          title={v.blocker_note ? `Blocked: ${v.blocker_note} — click to clear` : 'Flag a blocker'}
        >
          <Flag size={15} weight={v.blocker_note ? 'fill' : 'regular'} />
        </button>
      </div>
    </div>
  );
}
